import { TenantIsoStateIds } from 'TenantModule/constants';
import {
  selectTenantHash,
  selectTenantOrder,
  selectTenantIsoStates,
  selectDefaultTenant,
} from 'TenantModule/selectors';
import { Tenant } from 'TenantModule/types';
import ApiClientManager from 'core/apiClient';
import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuthUser } from 'selectors';
import { archiveTenant, fetchTenants } from 'thunk';
import { getIsTenantEnabled } from 'utils/feature';
import { useNotification } from 'hooks/useNotification';

export const useTenants = ({ isOff = true }: { isOff?: boolean } = {}) => {
  const dispatch = useDispatch();
  const isoStates = useSelector(selectTenantIsoStates);
  const defaultTenant = useSelector(selectDefaultTenant);
  const user = useSelector(selectAuthUser);
  const tenantsHash = useSelector(selectTenantHash);
  const tenantOrder = useSelector(selectTenantOrder);
  const { showError } = useNotification();

  useEffect(() => {
    // Only fetch once when needed; avoid re-fetching on each mount/render
    if (!isOff && getIsTenantEnabled() && (tenantOrder?.length || 0) === 0) {
      // TODO: Fix with Redux Toolkit configureStore migration
      dispatch(fetchTenants({}) as any);
    }
  }, [dispatch, isOff, tenantOrder?.length]);

  const getTenant = useCallback(
    ({ tenantId }: { tenantId: Tenant['id'] }): Tenant | undefined => {
      if (tenantId === defaultTenant?.id) {
        return defaultTenant;
      }

      return tenantsHash[tenantId];
    },
    [tenantsHash, defaultTenant],
  );

  const getTenantByIsoState = useCallback(
    ({ isoStateId }: { isoStateId: TenantIsoStateIds }) => {
      return isoStates[isoStateId] || {};
    },
    [isoStates],
  );

  const bulkArchiveTenant = useCallback(
    ({
      tenantIds,
      force = false,
      onArchiveTenantSuccess,
    }: {
      tenantIds: number[];
      /** Overrides the server's live-realm guard. Single tenant only (GLX-1819). */
      force?: boolean;
      onArchiveTenantSuccess?: VoidFunction;
    }) => {
      // Fire the caller's callback ONCE, after EVERY per-tenant request settles —
      // firing inside each onSuccess cleared the table selection on the first
      // completion while sibling archives were still in flight (and re-fired per
      // tenant). Only fire when at least one archive succeeded: on an all-fail
      // bulk the selection stays put for a retry, and each failure has its own
      // error toast.
      let remaining = tenantIds.length;
      let anySucceeded = false;
      const settle = (succeeded: boolean) => {
        anySucceeded = anySucceeded || succeeded;
        remaining -= 1;
        if (remaining === 0 && anySucceeded) {
          onArchiveTenantSuccess?.();
        }
      };
      tenantIds.forEach((tenantId) => {
        const tenant = getTenant({ tenantId });
        if (!tenant) {
          settle(false);
          return;
        }
        // TODO: Fix with Redux Toolkit configureStore migration
        dispatch(
          archiveTenant({
            tenant: tenant?.tenantName,
            archiver: user?.account?.name || '',
            // The server rejects bulk force; only a single-tenant archive may
            // override the live-realm guard.
            force: force && tenantIds.length === 1,
            meta: {
              onSuccess: () => {
                settle(true);
              },
              onFailure: ({ error }: { error?: any } = {}) => {
                // Surface each tenant's rejection (409 live realms / already
                // archived) — previously failures in the bulk flow were silent.
                const apiMessage =
                  error?.data?.message || error?.response?.data?.message || error?.message;
                const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '';
                showError(`Failed to archive ${tenant.tenantName}${apiMessageSuffix}`);
                settle(false);
              },
            },
          }) as any,
        );
      });
    },
    [dispatch, getTenant, showError, user?.account?.name],
  );

  /**
   * @deprecated Auth defaults are now immutable after login.
   * For context switching, navigate with URL params instead:
   * navigate(`/teams-2?tenant=${tenant.tenantName}&realmId=${realmId}`)
   *
   * This function is kept for backward compatibility but is a no-op.
   */
  const handleSelectGlobalTenant = useCallback(({ tenant: _tenant }: { tenant: Tenant }) => {
    // No-op: Auth defaults are immutable after login
    // Context switching happens via URL navigation
  }, []);

  // Use getAuthDefaultTenant to avoid deprecation warning
  const selectedGlobalTenant = getTenant({
    tenantId: ApiClientManager.getAuthDefaultTenant().id,
  });

  return {
    tenantsHash,
    tenantOrder,
    bulkArchiveTenant,
    selectedGlobalTenant,
    getTenant,
    getTenantByIsoState,
    defaultTenant,
    handleSelectGlobalTenant,
    isUsingNonDefaultTenant: getIsTenantEnabled() ? !selectedGlobalTenant?.isDefault : false,
  };
};
